import { useToast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";

const useAffirmCheckout = (priceDetails, orderDetails, setIsProcessing, orderId) => {
  const { toast } = useToast();
  const { email: reduxEmail } = useSelector((state) => state.userDetails);
  const toCents = (value) => Math.round(Number(value) * 100);

  const affirmCheckoutHandler = async () => {
    if (!window.affirm) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Affirm is not initialized. Ensure Affirm script is loaded.",
      });
      return;
    }
    if (!orderId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Order ID is missing. Please try again or contact support.",
      });
      return;
    }

    const email = reduxEmail || orderDetails?.email;
    const shipping = {
      name: {
        first: orderDetails?.address?.firstName,
        last: orderDetails?.address?.lastName,
      },
      address: {
        line1: orderDetails?.address?.address1,
        line2: orderDetails?.address?.address2 || "",
        city: orderDetails?.address?.city,
        state: orderDetails?.address?.state,
        zipcode: orderDetails?.address?.zipCode,
        country: "USA",
      },
      phone_number: orderDetails?.address?.mobile,
      email,
    };

    let billing = null;
    if (orderDetails.useAsBillingAddress) {
      billing = shipping;
    } else {
      const billingSource = orderDetails.billAdd || orderDetails.billingAddress;
      if (billingSource) {
        billing = {
          name: {
            first: billingSource?.firstName,
            last: billingSource?.lastName,
          },
          address: {
            line1: billingSource?.address1,
            line2: billingSource?.address2 || "",
            city: billingSource?.city,
            state: billingSource?.state,
            zipcode: billingSource?.zipCode,
            country: "USA",
          },
          phone_number: billingSource?.mobile,
          email,
        };
      }
    }

    const baseUrl = process.env.NEXT_PUBLIC_FRONTEND_BASE_URL;
    const items = [
      ...(orderDetails.deliverProducts || []).map((product) => ({
        display_name: product?.productName,
        sku: product?.sku,
        unit_price: toCents(product?.price_details?.displayPrice || 0),
        qty: product?.quantity || 1,
        item_image_url: product?.thumbnail,
        item_url: `${baseUrl}${product?.seoPdpUrl}`,
      })),
      ...(orderDetails.pickUpProducts || []).map((product) => ({
        display_name: product?.product?.productName,
        sku: product?.sku,
        unit_price: toCents(product?.product?.price_details?.displayPrice || 0),
        qty: product?.quantity || 1,
        item_image_url: product?.product?.thumbnail,
        item_url: `${baseUrl}${product?.product?.seoPdpUrl}`,
      })),
    ];

    if (items.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No items found in the order.",
      });
      return;
    }

    const totalAmount = priceDetails.grandTotal;
    const checkoutConfig = {
      merchant: {
        public_api_key: process.env.NEXT_PUBLIC_AFFIRM_SCRIPT_API_KEY,
        user_confirmation_url: `${process.env.NEXT_PUBLIC_API_BASE_URL}/affirmPayment?orderId=${orderId}`,
        user_cancel_url: `${baseUrl}cart`,
        name: "Airport Appliance, Inc.",
      },
      metadata:{
        mode:"modal"
      },
      shipping,
      items,
      order_id: orderId,
      currency: "USD",
      shipping_amount: toCents(priceDetails.totalDeliveryFee),
      tax_amount: toCents(priceDetails.tax),
      total: toCents(totalAmount),
    };

    if (billing) {
      checkoutConfig.billing = billing;
    }

    console.log("Checkout Config: ", JSON.stringify(checkoutConfig, null, 2));
    try {
      setIsProcessing(true);
      window.affirm.checkout(checkoutConfig);
      window.affirm.checkout.open();
    } catch (error) {
      console.error("Affirm checkout error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred during Affirm checkout.",
      });
      setIsProcessing(false);
    }
  };

  return {
    affirmCheckoutHandler,
  };
};

export default useAffirmCheckout;
